import { ServiceDetailLayout } from '../../components/ServiceDetailLayout';
import { SERVICES_INDEX, SERVICE_HERO_IMAGES } from '../../lib/servicesIndex';
import { Briefcase, ScrollText, Buildings, Info } from '@phosphor-icons/react';

const SpecialtySection = () => (
  <section className="py-20 bg-white border-t border-[#E8E2D5]">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-14 max-w-2xl mx-auto">
        <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
          Specialty Services
        </p>
        <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#800000] leading-tight">
          Beyond the seal — <span className="italic">a little extra help.</span>
        </h2>
        <p className="text-[#5C5C5C] text-base sm:text-[15px] mt-4 leading-relaxed">
          India occasionally helps clients with adjacent tasks that pair naturally with notary work — particularly for small-business owners and contractors operating across Middle Tennessee counties.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-[#FBF8F1] border border-[#E8E2D5] rounded-sm p-7 hover:border-[#D4AF37]/50 transition-all">
          <div className="flex items-start gap-3 mb-3">
            <Buildings weight="duotone" size={26} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <h3 className="font-serif-display text-lg font-semibold text-[#800000] leading-tight">
              Tennessee County Business License Assistance
            </h3>
          </div>
          <p className="text-[#3C3C3C] text-sm leading-relaxed pl-9">
            India can help small businesses and contractors obtain the specific county business licenses required for local projects within Tennessee — paperwork prep, courthouse runs, and signature coordination so you can keep working on the project itself.
          </p>
        </div>

        <div className="bg-[#FBF8F1] border border-[#E8E2D5] rounded-sm p-7 hover:border-[#D4AF37]/50 transition-all">
          <div className="flex items-start gap-3 mb-3">
            <Briefcase weight="duotone" size={26} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <h3 className="font-serif-display text-lg font-semibold text-[#800000] leading-tight">
              Vehicle Buyback &amp; Inspection Notarizations
            </h3>
          </div>
          <p className="text-[#3C3C3C] text-sm leading-relaxed pl-9">
            India regularly handles vehicle buyback paperwork and inspection-related notarizations for dealerships, lemon-law claims, and private buyback transactions — title transfers, odometer disclosures, and condition affidavits coordinated end-to-end.
          </p>
        </div>
      </div>

      {/* Referral disclaimer */}
      <div className="mt-8 bg-[#800000]/[0.04] border-l-4 border-[#D4AF37] rounded-sm p-6 flex items-start gap-4 max-w-4xl mx-auto">
        <Info weight="fill" size={22} className="text-[#800000] flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-serif-display text-base font-semibold text-[#800000] mb-1">
            A note on full business formation
          </p>
          <p className="text-[#3C3C3C] text-sm leading-relaxed">
            For comprehensive, full-scale business formation, entity structuring, or multi-state registrations, India refers clients to trusted specialists in her professional network. Her work here is focused on partial / county-level licensing and notarial support — not full corporate formation.
          </p>
        </div>
      </div>
    </div>
  </section>
);

export const GeneralNotaryPage = () => (
  <ServiceDetailLayout
    eyebrow="General Mobile Notary"
    title="A Tennessee notary who"
    titleAccent="comes to you."
    subtitle="Affidavits, sworn statements, vehicle titles, vehicle buybacks &amp; inspections, parental travel consent, custody letters, school documents — any single notarial act, performed at your kitchen table, your office, or your favorite coffee shop."
    heroImage={SERVICE_HERO_IMAGES['general-notary']}
    intro={{
      kicker: 'Single & Multi-Document',
      heading: 'The everyday notary, delivered with corporate-grade care.',
      body: [
        'Most notarial acts are routine — but the hassle of finding a bank notary, taking time off work, and parking downtown is anything but. India eliminates the friction. She comes to you, brings the seal, brings the patience, and gets you back to your day.',
        "Whether it's one sheet of paper or a small stack, whether it's tonight after dinner or 7 AM before the school run — India shows up prepared, ID-checked, and on time.",
      ],
      pullQuote: {
        text: 'I needed a parental-consent travel form notarized at 9 PM on a Sunday. India was at my door in 35 minutes.',
        attribution: 'Same-Day Client · Nashville',
      },
    }}
    bullets={[
      { title: 'Affidavits & Sworn Statements', description: 'Court filings, name changes, address verifications — properly executed and witness-coordinated.' },
      { title: 'Vehicle Titles & Bills of Sale', description: 'Tennessee title transfers, out-of-state purchases, and lienholder releases handled correctly.' },
      { title: 'Vehicle Buybacks & Inspections', description: 'Dealership buyback paperwork, lemon-law claims, and inspection-related notarizations — coordinated end-to-end.' },
      { title: 'Parental Consent for Travel', description: 'Notarized travel-permission letters for minors traveling without one or both parents.' },
      { title: 'School & Custody Documents', description: 'Permission slips, custody verifications, residency affidavits — common, sensitive, handled with care.' },
      { title: 'Permission Letters & Authorizations', description: 'Single-page authorizations for medical care, package pickup, financial decisions, and more.' },
      { title: 'Same-Day & Evening Hours', description: 'Most appointments scheduled in under 60 minutes, including nights and weekends.' },
    ]}
    extraSection={<SpecialtySection />}
    faqs={[
      { q: 'How much does a general notary visit cost?', a: 'Tennessee statute caps notarial-act fees per signature; India also charges a transparent travel fee based on distance. Call or text for an exact quote — most one-document visits run a flat, all-in rate.' },
      { q: 'Do I need to bring my own witnesses?', a: 'For documents requiring witnesses (some POAs, healthcare directives, etc.), India can either coordinate witnesses for you or guide you on who in your household qualifies.' },
      { q: 'What ID do you need to see?', a: 'A valid, unexpired government-issued photo ID — Tennessee driver\'s license, U.S. passport, military ID, or state-issued ID card.' },
      { q: 'Can you notarize for someone in the hospital?', a: 'Absolutely. India regularly visits hospitals, hospices, and senior-living communities throughout Nashville for bedside notarization.' },
      { q: 'Do you handle vehicle buyback paperwork?', a: 'Yes — India regularly notarizes vehicle buyback paperwork (including lemon-law and dealership buybacks) along with the related title transfers, odometer disclosures, and inspection affidavits.' },
      { q: 'Can you help me get a Tennessee county business license?', a: 'India can help with partial business licensing — specifically county-level business licenses required for local Tennessee projects. For full corporate formation or multi-state registration, she refers clients out to trusted specialists.' },
    ]}
    relatedSlugs={['estate-planning', 'apostille', 'i9-verification']}
    servicesIndex={SERVICES_INDEX}
  />
);
